import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popover, Switch, Tabs, Text, useTheme } from '@Sage/hoi-poi-ui';

import {
    getAvailableDevEnvironments,
    getDevEnvironmentOverride,
    getStartupDevEnvironment,
    hasDevEnvironmentOverride,
    isDevEnvironmentSwitcherEnabled,
    selectDevEnvironment,
    setDevEnvironment,
} from './environments';
import './styles.scss';

const TAB_ENVIRONMENTS = 'environments';

const OverrideSparkles = () => (
    <>
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
    </>
);

export const DevToolsTabTitle = memo(({ icon, label }) => (
    <span className="fm-dev__popup__tab-title">
        <Icon name={icon} size="small" />
        {label}
    </span>
));

DevToolsTabTitle.displayName = 'DevToolsTabTitle';

DevToolsTabTitle.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};

export const DevToolsSwitchRow = memo(({ checked, description, isOverridden, label, onChange }) => (
    <div className={`fm-dev__popup__row${isOverridden ? ' fm-dev__popup__row--override' : ''}`}>
        <div className="fm-dev__popup__row-copy">
            <Text type="caption" bold>
                {label}
            </Text>
            {description ? (
                <Text type="caption" className="fm-dev__popup__row-desc">
                    {description}
                </Text>
            ) : null}
        </div>
        <Switch aria-label={label} size="small" checked={checked} onChange={onChange} />
    </div>
));

DevToolsSwitchRow.displayName = 'DevToolsSwitchRow';

DevToolsSwitchRow.propTypes = {
    checked: PropTypes.bool,
    description: PropTypes.string,
    isOverridden: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

const DevTools = memo(({ enabled, extraTabs = [], hasExtraOverride = false, onSelect }) => {
    const theme = useTheme();
    const overrideEnvironment = useMemo(() => getDevEnvironmentOverride(), []);
    const startupEnvironment = useMemo(() => getStartupDevEnvironment(), []);
    const effectiveEnvironment = overrideEnvironment || startupEnvironment || '';
    const hasEnvOverride = useMemo(() => hasDevEnvironmentOverride(), []);
    const hasAnyOverride = hasEnvOverride || hasExtraOverride;
    const [activeTab, setActiveTab] = useState(TAB_ENVIRONMENTS);
    const selectEnvironment = useCallback(
        (name) => {
            selectDevEnvironment(name);
            onSelect?.(getDevEnvironmentOverride());
            window.location.reload();
        },
        [onSelect],
    );
    const resetEnvironment = useCallback(() => {
        setDevEnvironment('');
        onSelect?.('');
        window.location.reload();
    }, [onSelect]);
    const toolsTabs = useMemo(
        () => [
            {
                key: TAB_ENVIRONMENTS,
                title: <DevToolsTabTitle icon="world" label="Environments" />,
                content: (
                    <div className="fm-dev__popup__buttons">
                        <Text type="caption">
                            {startupEnvironment
                                ? `Startup: ${startupEnvironment}`
                                : 'Startup configuration'}
                        </Text>
                        <Text type="caption">
                            {hasEnvOverride
                                ? `Active override: ${overrideEnvironment}`
                                : 'No environment override'}
                        </Text>
                        {getAvailableDevEnvironments().map((env) => (
                            <Button
                                key={env.name}
                                className="fm-dev__popup__env-btn"
                                size="medium"
                                type={env.name === effectiveEnvironment ? 'primary' : 'secondary'}
                                onClick={() => selectEnvironment(env.name)}
                            >
                                {env.name}
                            </Button>
                        ))}
                        <Button
                            size="small"
                            type="secondary"
                            isDisabled={!hasEnvOverride}
                            onClick={resetEnvironment}
                        >
                            Reset to startup
                        </Button>
                    </div>
                ),
            },
            ...extraTabs,
        ],
        [
            extraTabs,
            effectiveEnvironment,
            hasEnvOverride,
            overrideEnvironment,
            resetEnvironment,
            selectEnvironment,
            startupEnvironment,
        ],
    );
    const toolsContent = useMemo(
        () => (
            <div className="fm-dev__popup__tools">
                <div className="fm-dev__popup__header">
                    <span className="fm-dev__popup__header-icon">
                        <Icon name="tool" size="medium" color={theme.colors.primary.white} />
                    </span>
                    <div className="fm-dev__popup__header-copy">
                        <Text type="subtitle" color={theme.colors.primary.white}>
                            Dev Tools
                        </Text>
                        <Text type="caption" color={theme.colors.primary.white}>
                            Local overrides for this browser
                        </Text>
                    </div>
                </div>
                <Tabs
                    className="fm-dev__popup__tabs"
                    classes={{
                        content: 'fm-dev__popup__tabs-content',
                        tabPane: 'fm-dev__popup__tab-pane',
                        tabsContainer: 'fm-dev__popup__tabs-shell',
                    }}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabs={toolsTabs}
                />
            </div>
        ),
        [activeTab, theme.colors.primary.white, toolsTabs],
    );
    const active = enabled ?? isDevEnvironmentSwitcherEnabled();

    if (!active) return null;

    return (
        <div className="fm-dev__popup">
            <Popover placement="top" content={toolsContent} overlayInnerStyle={{ padding: 0 }}>
                <div
                    className={`fm-dev__popup__tag${
                        hasAnyOverride ? ' fm-dev__popup__tag--override' : ''
                    }`}
                >
                    {hasAnyOverride && <OverrideSparkles />}
                    <span className="fm-dev__popup__tag-inner">
                        <span className="fm-dev__popup__tag-icon">
                            <Icon name="tool" size="small" color={theme.colors.primary.white} />
                        </span>
                        <Text color={theme.colors.primary.white}>
                            {effectiveEnvironment
                                ? `DEV TOOLS · ${effectiveEnvironment}`
                                : 'DEV TOOLS'}
                        </Text>
                    </span>
                </div>
            </Popover>
        </div>
    );
});

DevTools.displayName = 'DevTools';

DevTools.propTypes = {
    enabled: PropTypes.bool,
    extraTabs: PropTypes.arrayOf(
        PropTypes.shape({
            content: PropTypes.node,
            key: PropTypes.string.isRequired,
            title: PropTypes.node.isRequired,
        }),
    ),
    hasExtraOverride: PropTypes.bool,
    onSelect: PropTypes.func,
};

export default DevTools;
